import * as Sentry from "@sentry/react";

import { Paragraph, StyledAlert } from "App/global-style";
import {
  nullableBooleanToNullableFormBoolean,
  nullableFormBooleanToNullableBoolean,
} from "Utils/formUtils";
import { Form, FormikProps } from "formik";
import { useDispatch, useSelector } from "react-redux";
import {
  birthDateLabel,
  maxDate,
  minDate,
  multipleChildrenLabel,
  notWithinIntervalError,
} from "./pregnantAndNewbornProductDetailsUtils";

import { ProductDetailsTile } from "Components/Features/CheckPrice/Tiles/ProductDetailsTile";
import { SystemErrorAlert } from "Components/Form/Alert/SystemErrorAlert";
import { Datepicker } from "Components/Form/Datepicker";
import { RadioButtonTrueFalse } from "Components/Form/RadioButton/RadioButtonTrueFalse";
import { InputFailureTracker } from "Components/InputFailureTracker";
import { NavigationButtons } from "Components/NavigationButtons";
import { BackendProductInfo } from "Features/CheckPrice/Products/productConstants";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { InsuranceProductPriceRequestInfo } from "Store/interfaces/products/checkPrice";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { getError } from "Utils/productUtils";
import React from "react";
import { PREGNANT_AND_NEWBORN_ABOUT_CHILD_STEP } from "..";
import { childBornFromQueryParams } from "../pregnantAndNewbornUtils";
import { PregnantAndNewbornDetailsFormValues } from "./pregnantAndNewbornProductDetailsInterfaces";
import { validationSchema } from "./pregnantAndNewbornProductDetailsValidation";

interface Props {
  updatePriceRequest: (
    productPriceUrl: string,
    stepForwardOnSuccess: boolean,
    updatedProductPriceRequestInfo: InsuranceProductPriceRequestInfo,
  ) => void;
}

export const PregnantAndNewbornProductDetails = ({
  updatePriceRequest,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const preSelectedChildBorn = childBornFromQueryParams();

  const initialFormState = useFormState(
    PREGNANT_AND_NEWBORN_ABOUT_CHILD_STEP.id,
    {
      childBorn: nullableBooleanToNullableFormBoolean(preSelectedChildBorn),
      multipleChildren: undefined,
      birthDate: undefined,
    },
  );

  const [
    {
      formText: { aboutChild },
    },
    lang,
  ] = useProductTexts(
    getFMSProductName(INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN),
  );
  const {
    priceCalculation: { priceCalculationResult },
  } = useSelector((state: ReduxState) => state);

  const handleSubmit = (
    formValues: PregnantAndNewbornDetailsFormValues,
  ): void => {
    dispatch(
      updatePriceCalculationFormState({
        stepId: PREGNANT_AND_NEWBORN_ABOUT_CHILD_STEP.id,
        formState: {
          ...formValues,
          childBorn: formValues.childBorn,
          multipleChildren: formValues.multipleChildren,
        },
      }),
    );
    updatePriceRequest(
      BackendProductInfo.PREGNANT_AND_NEWBORN.priceReqUrl,
      true,
      {
        pregnant: !nullableFormBooleanToNullableBoolean(formValues.childBorn),
        multipleChildren: nullableFormBooleanToNullableBoolean(
          formValues.multipleChildren,
        ),
      },
    );
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductDetails - ${INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN}`,
        );
      }}
    >
      <ProductDetailsTile
        header={PREGNANT_AND_NEWBORN_ABOUT_CHILD_STEP.title}
        validationSchema={validationSchema()}
        initialValues={initialFormState}
        onSubmit={handleSubmit}
      >
        {(formProps: FormikProps<PregnantAndNewbornDetailsFormValues>) => {
          const intervalError = notWithinIntervalError(
            aboutChild.birthDate,
            lang,
            formProps.values.birthDate,
            formProps.values.childBorn,
          );

          return (
            <Form>
              <Paragraph>{aboutChild.about[lang]}</Paragraph>
              {preSelectedChildBorn === undefined && (
                <RadioButtonTrueFalse
                  direction="vertical"
                  name="childBorn"
                  id="childBorn"
                  hint={aboutChild.childBorn.hint[lang]}
                  label={aboutChild.childBorn.label[lang]}
                  error={getError("childBorn", formProps)}
                  options={[
                    {
                      id: "childBorn-yes",
                      label: `${aboutChild.childBorn.options.yes[lang]}`,
                      value: true,
                    },
                    {
                      id: "childBorn-no",
                      label: `${aboutChild.childBorn.options.no[lang]}`,
                      value: false,
                    },
                  ]}
                />
              )}
              <RadioButtonTrueFalse
                direction="vertical"
                name="multipleChildren"
                id="multipleChildren"
                hint={aboutChild.multipleChildren.hint[lang]}
                label={multipleChildrenLabel(
                  aboutChild.multipleChildren,
                  lang,
                  formProps.values.childBorn,
                )}
                error={getError("multipleChildren", formProps)}
                options={[
                  {
                    id: "multipleChildren-yes",
                    label: `${aboutChild.multipleChildren.options.yes[lang]}`,
                    value: true,
                  },
                  {
                    id: "multipleChildren-no",
                    label: `${aboutChild.multipleChildren.options.no[lang]}`,
                    value: false,
                  },
                ]}
              />
              <Datepicker
                id="birthDate"
                name="birthDate"
                label={birthDateLabel(
                  aboutChild.birthDate,
                  lang,
                  formProps.values.childBorn,
                  formProps.values.multipleChildren,
                )}
                hint={aboutChild.birthDate.hint[lang]}
                min={minDate(formProps.values.childBorn)}
                max={maxDate(formProps.values.childBorn)}
                error={getError("birthDate", formProps)}
                large
              />
              {formProps.values.birthDate && intervalError && (
                <StyledAlert
                  alertId="birthDate"
                  title={aboutChild.birthDate.alertTitle[lang]}
                  description={intervalError}
                  fluid
                />
              )}
              {priceCalculationResult.error && (
                <SystemErrorAlert alertId="priceCalculation" />
              )}
              <NavigationButtons isLoading={priceCalculationResult.isLoading} />
              <InputFailureTracker
                productName={INSURANCE_NAME_VALUE.PREGNANT_AND_NEWBORN}
                stepId={PREGNANT_AND_NEWBORN_ABOUT_CHILD_STEP.id}
              />
            </Form>
          );
        }}
      </ProductDetailsTile>
    </Sentry.ErrorBoundary>
  );
};
